import { useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { layoutModeAtom } from '../atoms/v3/uiAtoms';
import { useShortcutManager } from './useShortcutManager';
import {
  createGlobalShortcuts,
  createTaskV2Shortcuts,
  createChatShortcuts,
  TaskV2ShortcutActions,
  ChatShortcutActions
} from '../config/shortcuts';
import { useTaskV2ShortcutActions } from './useTaskV2ShortcutActions';
import { useChatShortcutActions } from './useChatShortcutActions';
import { useGlobalShortcutActions } from './useGlobalShortcutActions';

/**
 * 快捷键注册管理 Hook
 * 根据当前页面和状态动态注册/注销快捷键
 */
export const useShortcutRegistration = () => {
  const location = useLocation();
  const layoutMode = useAtomValue(layoutModeAtom);
  const { registerBatch, unregisterBatch, getAllCommands } = useShortcutManager();

  // 获取所有快捷键的业务逻辑（使用 useMemo 稳定化）
  const globalActions = useGlobalShortcutActions();
  const taskV2Actions = useTaskV2ShortcutActions();
  const chatActions = useChatShortcutActions();

  // 稳定化 actions 对象，避免不必要的重新注册
  const stableGlobalActions = useMemo(() => globalActions, [
    globalActions.smartChatSwitch,
    globalActions.smartTaskSwitch,
    globalActions.toggleLeftSidebar,
    globalActions.toggleRightSidebar,
    globalActions.closeModal
  ]);

  const stableTaskV2Actions = useMemo(() => taskV2Actions, [
    taskV2Actions.goToPrevDay,
    taskV2Actions.goToNextDay,
    taskV2Actions.goToToday,
    taskV2Actions.focusPrevTask,
    taskV2Actions.focusNextTask,
    taskV2Actions.toggleTask,
    taskV2Actions.editTask,
    taskV2Actions.newTask,
    taskV2Actions.toggleViewMode,
    taskV2Actions.resetFocus,
    taskV2Actions.undo,
    taskV2Actions.zoomIn,
    taskV2Actions.zoomOut,
    taskV2Actions.closeTaskDetail,
    taskV2Actions.isTaskDetailOpen
  ]);

  const stableChatActions = useMemo(() => ({
    toggleVoiceRecording: chatActions.toggleVoiceRecording,
    cancelRecording: chatActions.cancelRecording,
    isVoiceRecording: chatActions.isVoiceRecording
  }), [
    chatActions.toggleVoiceRecording,
    chatActions.cancelRecording,
    chatActions.isVoiceRecording
  ]);

  // 使用 useRef 存储已注册的命令ID，确保正确注销
  const registeredGlobalCommandIds = useRef<string[]>([]);
  const registeredPageCommandIds = useRef<string[]>([]);

  // 获取当前页面类型
  const getCurrentPageType = useCallback((): string => {
    const path = location.pathname;
    if (path.includes('/v3/taskv2')) return 'taskv2';
    if (path.includes('/v3/chat')) return 'chat';
    if (path.includes('/v3/me')) return 'me';
    return 'unknown';
  }, [location.pathname]);

  // 注册全局快捷键（使用真实业务逻辑）
  useEffect(() => {
    // 先注销之前的全局快捷键
    if (registeredGlobalCommandIds.current.length > 0) {
      console.log('ShortcutRegistration: Unregistering previous global shortcuts:', registeredGlobalCommandIds.current.length);
      unregisterBatch(registeredGlobalCommandIds.current);
      registeredGlobalCommandIds.current = [];
    }

    console.log('ShortcutRegistration: Registering global shortcuts with real actions');
    const globalShortcuts = createGlobalShortcuts(stableGlobalActions);
    registerBatch(globalShortcuts);

    // 存储命令ID用于后续注销
    registeredGlobalCommandIds.current = globalShortcuts.map(cmd => cmd.id);
    console.log('ShortcutRegistration: Registered global shortcuts:', registeredGlobalCommandIds.current.length);

    return () => {
      console.log('ShortcutRegistration: Cleanup - Unregistering global shortcuts');
      if (registeredGlobalCommandIds.current.length > 0) {
        unregisterBatch(registeredGlobalCommandIds.current);
        registeredGlobalCommandIds.current = [];
      }
    };
  }, [registerBatch, unregisterBatch, stableGlobalActions]);

  // 根据页面类型注册页面特定快捷键
  useEffect(() => {
    // 先注销之前的页面快捷键
    if (registeredPageCommandIds.current.length > 0) {
      console.log('ShortcutRegistration: Unregistering previous page shortcuts:', registeredPageCommandIds.current.length);
      unregisterBatch(registeredPageCommandIds.current);
      registeredPageCommandIds.current = [];
    }

    // 直接在 useEffect 内部计算页面类型，避免依赖函数
    const path = location.pathname;
    const currentPage = path.includes('/v3/taskv2') ? 'taskv2'
                      : path.includes('/v3/chat') ? 'chat'
                      : path.includes('/v3/me') ? 'me'
                      : 'unknown';

    const shortcutsToRegister = [];

    console.log(`ShortcutRegistration: Registering shortcuts for page: ${currentPage}, layout: ${layoutMode}`);

    // 根据页面类型添加对应的快捷键
    if (currentPage === 'taskv2') {
      // 使用带有真实业务逻辑的 TaskV2 快捷键
      const taskV2Shortcuts = createTaskV2Shortcuts(stableTaskV2Actions);
      shortcutsToRegister.push(...taskV2Shortcuts);
    }

    // 在桌面端，聊天快捷键在所有页面都可用（因为右栏始终有聊天）
    // 在移动端，只在聊天页面可用
    if (currentPage === 'chat' || layoutMode === 'desktop') {
      // 使用带有真实业务逻辑的 Chat 快捷键
      const chatShortcuts = createChatShortcuts(stableChatActions);
      shortcutsToRegister.push(...chatShortcuts);
    }

    if (shortcutsToRegister.length > 0) {
      registerBatch(shortcutsToRegister);
      // 存储命令ID用于后续注销
      registeredPageCommandIds.current = shortcutsToRegister.map(cmd => cmd.id);
      console.log('ShortcutRegistration: Registered page shortcuts:', registeredPageCommandIds.current.length);
    }

    return () => {
      console.log('ShortcutRegistration: Cleanup - Unregistering page shortcuts');
      if (registeredPageCommandIds.current.length > 0) {
        unregisterBatch(registeredPageCommandIds.current);
        registeredPageCommandIds.current = [];
      }
    };
  }, [location.pathname, layoutMode, registerBatch, unregisterBatch, stableTaskV2Actions, stableChatActions]);

  // 增强的调试功能：打印当前注册的快捷键和去重信息
  const debugShortcuts = useCallback(() => {
    const commands = getAllCommands();
    const currentPage = getCurrentPageType();

    console.group('🔍 Shortcut Registration Debug Info');
    console.log(`📊 Total commands: ${commands.length}`);
    console.log(`📍 Current page: ${currentPage}`);
    console.log(`🖥️ Layout mode: ${layoutMode}`);

    // 按来源分组
    const bySource = commands.reduce((acc, cmd) => {
      let source = 'unknown';
      if (cmd.id.startsWith('global.')) source = 'global';
      else if (cmd.id.startsWith('taskv2.')) source = 'taskv2';
      else if (cmd.id.startsWith('chat.')) source = 'chat';
      else if (cmd.id.startsWith('help.')) source = 'help';

      if (!acc[source]) acc[source] = [];
      acc[source].push(cmd);
      return acc;
    }, {} as Record<string, typeof commands>);

    console.group('📋 Commands by Source');
    Object.entries(bySource).forEach(([source, cmds]) => {
      console.group(`${source} (${cmds.length} commands)`);
      cmds.forEach(cmd => {
        const conditionStatus = cmd.condition ?
          (cmd.condition({ layoutMode, currentPage, isInputFocused: false, activeElement: null, isModalOpen: false, isVoiceRecording: false, event: new KeyboardEvent('keydown') }) ? '✅' : '❌')
          : '⚪';
        console.log(`${conditionStatus} ${cmd.keys.join('/')} - ${cmd.description} (Priority: ${cmd.priority})`);
      });
      console.groupEnd();
    });
    console.groupEnd();

    // 按分类分组
    const byCategory = commands.reduce((acc, cmd) => {
      if (!acc[cmd.category]) acc[cmd.category] = [];
      acc[cmd.category].push(cmd);
      return acc;
    }, {} as Record<string, typeof commands>);

    console.group('🏷️ Commands by Category');
    Object.entries(byCategory).forEach(([category, cmds]) => {
      console.group(`${category} (${cmds.length} commands)`);
      cmds.forEach(cmd => {
        console.log(`${cmd.keys.join('/')} - ${cmd.description} (ID: ${cmd.id})`);
      });
      console.groupEnd();
    });
    console.groupEnd();

    // 检查重复键位
    const keyMap = new Map<string, ShortcutCommand[]>();
    commands.forEach(cmd => {
      cmd.keys.forEach(key => {
        const normalizedKey = key.toLowerCase();
        if (!keyMap.has(normalizedKey)) {
          keyMap.set(normalizedKey, []);
        }
        keyMap.get(normalizedKey)!.push(cmd);
      });
    });

    const duplicateKeys = Array.from(keyMap.entries()).filter(([, cmds]) => cmds.length > 1);
    if (duplicateKeys.length > 0) {
      console.group('⚠️ Duplicate Key Bindings');
      duplicateKeys.forEach(([key, cmds]) => {
        console.log(`Key "${key}" used by ${cmds.length} commands:`, cmds.map(cmd => `${cmd.id} (priority: ${cmd.priority})`));
      });
      console.groupEnd();
    } else {
      console.log('✅ No duplicate key bindings detected');
    }

    console.groupEnd();
  }, [getAllCommands, getCurrentPageType, layoutMode]);

  return {
    getCurrentPageType,
    debugShortcuts
  };
};

/**
 * 开发环境下的快捷键调试工具
 */
export const useShortcutDebugger = () => {
  const { debugShortcuts } = useShortcutRegistration();

  useEffect(() => {
    // 在开发环境下，添加全局调试函数
    if (process.env.NODE_ENV === 'development') {
      (window as any).debugShortcuts = debugShortcuts;
      console.log('Shortcut debugger available: window.debugShortcuts()');
    }

    return () => {
      if (process.env.NODE_ENV === 'development') {
        delete (window as any).debugShortcuts;
      }
    };
  }, [debugShortcuts]);
};
